import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useFetchTriggerOptions from 'components/domain/automation/triggers/useFetchTriggerOptions';
import CustomSelectField from 'components/forms/customSelectField/CustomSelectField';
import Tooltip from 'components/tooltip/Tooltip';
import { TriggerActionType } from 'models/automation/Triggers';
import { orderBy } from 'utils/arrayUtils';
import { TriggerAddEditModalFormValues } from '../TriggerAddEditModal.schema';
import {
  isCreateJobDisabled,
  isCreateReviewDisabled,
  isDirectMessageDisabled,
  isSmsDisabled,
  isWhatsAppDisabled,
} from '../TriggerAddEditModal.utils';

const TriggerAddEditModalActionTypeField = () => {
  const { t } = useTranslation();
  const { data: triggerOptions } = useFetchTriggerOptions();
  const { control, setValue } = useFormContext<TriggerAddEditModalFormValues>();
  const conditions = useWatch({ control, name: 'conditions' });
  const eventType = useWatch({ control, name: 'eventType' });
  const timeCondition = useWatch({ control, name: 'timeCondition' });

  const getLabel = ({
    type,
    isDisabled,
  }: {
    type: TriggerActionType;
    isDisabled: boolean;
  }) => {
    const label = t('pageAutomations.triggers.actionType', { context: type });

    if (isDisabled) {
      return {
        label: (
          <Tooltip
            id={`action-type-${type}`}
            text={t('pageAutomations.triggers.actionTypeDisabled', {
              context: type,
            })}
          >
            {label}
          </Tooltip>
        ),
        value: type,
        isDisabled,
      };
    }

    return {
      label,
      value: type,
      isDisabled,
    };
  };

  const actionOptions = [
    getLabel({
      isDisabled: isCreateJobDisabled(eventType),
      type: TriggerActionType.CREATE_JOB,
    }),
    getLabel({
      isDisabled: isCreateReviewDisabled(timeCondition, conditions),
      type: TriggerActionType.CREATE_REVIEW,
    }),
    getLabel({
      isDisabled: isDirectMessageDisabled(conditions),
      type: TriggerActionType.DIRECT_MESSAGE,
    }),
    getLabel({
      isDisabled: false,
      type: TriggerActionType.EMAIL,
    }),
    getLabel({
      isDisabled: isSmsDisabled(triggerOptions),
      type: TriggerActionType.SMS,
    }),
    getLabel({
      isDisabled: isWhatsAppDisabled(triggerOptions),
      type: TriggerActionType.WHATSAPP,
    }),
  ];

  const options = [
    { label: t('pageAutomations.triggers.actionType'), value: '' },
    ...orderBy(actionOptions, [
      {
        field: ({ value }) =>
          t('pageAutomations.triggers.actionType', { context: value }),
        order: 'asc',
      },
    ]),
  ];

  const handleOnChange = () => {
    setValue('jobTiming', '', { shouldDirty: true });
    setValue('recipientsList', [], { shouldDirty: true });
    setValue('serviceUid', '', { shouldDirty: true });
    setValue('templateUid', '', { shouldDirty: true });
  };

  return (
    <CustomSelectField
      colSmInput={12}
      label={t('pageAutomations.triggers.modal.actionType')}
      labelOnTop
      name="actionType"
      onChangeHandler={handleOnChange}
      options={options}
      required
    />
  );
};

export default TriggerAddEditModalActionTypeField;
